import type { CompensationLine } from '@/types/compensation';

/** Marks the file as UTF-8 for spreadsheet apps that would otherwise guess the system codepage
 * and mangle accented names. */
const UTF8_BOM = '﻿';

/** The salary-import template's header row and column order. Fixed and untranslated: it is also
 * the exact shape `POST /{id}/Salaries/Parse` expects back, so translating it would make a
 * downloaded-then-reuploaded template fail to parse in a different language than it was
 * generated in. */
const TEMPLATE_HEADER = ['email', 'name', 'salary'] as const;

const CSV_SEPARATOR = ';';

/** Quotes a CSV cell and doubles any internal quote, so a name containing a semicolon or a quote
 * still round-trips through the parser. */
const quoteCell = (value: string): string => `"${value.replace(/"/g, '""')}"`;

/**
 * Builds the downloadable salary-import template from an envelope's current lines: the header row
 * `email;name;salary`, then one row per line with its current salary (or an empty cell when no salary is known yet).
 */
export const buildSalaryTemplate = (lines: CompensationLine[]): string => {
    const rows = [
        TEMPLATE_HEADER,
        ...lines.map((line) => [
            line.email,
            line.displayName,
            line.salary === null ? '' : String(line.salary),
        ]),
    ];

    const csvBody = rows.map((row) => row.map(quoteCell).join(CSV_SEPARATOR)).join('\r\n');

    return `${UTF8_BOM}${csvBody}`;
};
